import React, { type AriaAttributes } from 'react';
import type { WithIntlProps, WrappedComponentProps } from 'react-intl';
import { defineMessages, injectIntl } from 'react-intl';
import { styled } from '@compiled/react';
import debounce from 'lodash/debounce';

import Textfield from '@atlaskit/textfield/text-field';
import SearchIcon from '@atlaskit/icon/core/search';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Pressable from '@atlaskit/primitives/pressable';
import { xcss } from '@atlaskit/primitives/xcss';

export const i18n: {
	searchBarLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	searchPlaceholderText: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	searchIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	crossIconLabel: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	searchBarLabel: {
		id: 'debounced-search-bar.label',
		defaultMessage: 'Search',
		description: 'A visible label for the search bar that allows the user to filter items',
	},
	searchPlaceholderText: {
		id: 'debounced-search-bar.hintText',
		defaultMessage: 'Search',
		description: 'The placeholder text in the search box, visible when it is empty',
	},
	searchIconLabel: {
		id: 'debounced-search-bar.search.icon.label',
		defaultMessage: 'Search icon',
		description: 'The aria-label for the magnifying glass icon in the search bar',
	},
	crossIconLabel: {
		id: 'debounced-search-bar.cross.icon.label',
		defaultMessage: 'Clear search bar',
		description: 'The aria-label for the cross icon in the search bar',
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SearchIconWrapper = styled.div({
	margin: `1px ${token('space.075')} 0 ${token('space.100')}`,
});

const pressableStyles = xcss({
	marginTop: '0',
	marginBottom: '0',
	marginLeft: 'space.075',
	marginRight: 'space.075',
	padding: '0',
	background: 'none',
	border: 'none',
});

const pressableDisabledStyle = xcss({
	cursor: 'inherit',
});

const pressableEnabledStyle = xcss({
	cursor: 'pointer',
});

type DebouncedSearchBarProps = {
	onChange: (searchQuery: string) => void;
	onFocus?: () => void;
	onBlur?: () => void;

	initialValue?: string;
	label?: string;
	placeholder?: string;
	testId?: string;
	debouncedTime?: number;

	isDisabled?: boolean;
	isCompact?: boolean;
	autoFocus?: boolean;
	ariaLabelledBy?: string;
	ariaLive?: AriaAttributes['aria-live'];
};

type DebouncedSearchBarState = {
	value: string;
};

type InjectedDebouncedSearchBarProps = WrappedComponentProps & DebouncedSearchBarProps;

class InjectedDebouncedSearchBar extends React.Component<
	InjectedDebouncedSearchBarProps,
	DebouncedSearchBarState
> {
	static defaultProps = {
		isCompact: true,
		debouncedTime: 250,
	};

	state = {
		value: this.props.initialValue || '',
	};

	searchBarRef = React.createRef<HTMLInputElement>();

	_debouncedUpdateSearch = debounce((newSearchQuery: string): void => {
		this.props.onChange(newSearchQuery);
	}, this.props.debouncedTime);

	handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
		this.setState({ value: e.target.value });
		this._debouncedUpdateSearch(e.target.value);
	};

	handleBlur = (): void => {
		const { onBlur } = this.props;
		this._debouncedUpdateSearch.flush();
		onBlur?.();
	};

	handleClearClick = (e) => {
		e.preventDefault();
		if (this.props.isDisabled) {
			return;
		}
		this.setState({ value: '' });
		this._debouncedUpdateSearch('');
		this._debouncedUpdateSearch.flush();

		this.searchBarRef.current?.focus();
	};

	render() {
		const {
			intl,
			onFocus,
			label = intl.formatMessage(i18n.searchBarLabel),
			placeholder = intl.formatMessage(i18n.searchPlaceholderText),
			testId,
			isDisabled,
			isCompact,
			autoFocus,
			ariaLabelledBy,
			ariaLive,
		} = this.props;
		const { value } = this.state;

		return (
			<Textfield
				ref={this.searchBarRef}
				label={label}
				testId={testId}
				placeholder={placeholder}
				value={value}
				onChange={this.handleChange}
				onBlur={this.handleBlur}
				elemBeforeInput={
					<SearchIconWrapper>
						<SearchIcon
							label={intl.formatMessage(i18n.searchIconLabel)}
							color="var(--ds-icon-subtle)"
						/>
					</SearchIconWrapper>
				}
				elemAfterInput={
					<Pressable
						xcss={[pressableStyles, isDisabled ? pressableDisabledStyle : pressableEnabledStyle]}
						testId="clear-button"
						onClick={this.handleClearClick}
						isDisabled={isDisabled}
					>
						{!!value ? (
							<CrossCircleIcon
								label={intl.formatMessage(i18n.crossIconLabel)}
								color={isDisabled ? token('color.icon.disabled') : token('color.icon.subtle')}
							/>
						) : (
							<VisuallyHidden>
								<CrossCircleIcon
									label={intl.formatMessage(i18n.crossIconLabel)}
									color={isDisabled ? token('color.icon.disabled') : token('color.icon.subtle')}
								/>
							</VisuallyHidden>
						)}
					</Pressable>
				}
				isDisabled={isDisabled}
				onFocus={onFocus}
				isCompact={isCompact}
				autoFocus={autoFocus}
				aria-labelledby={ariaLabelledBy}
				aria-live={ariaLive}
			/>
		);
	}
}

export const DebouncedSearchBar: React.FC<WithIntlProps<InjectedDebouncedSearchBarProps>> & {
	WrappedComponent: React.ComponentType<InjectedDebouncedSearchBarProps>;
} = injectIntl(InjectedDebouncedSearchBar);
